/**
 * אחסון צד־לקוח בלבד ל־Pricing Engine audit — ללא API / DB.
 */

import { compactPricingAuditMeta } from "./compactPricingAuditMeta.js";
import { resolvePricingAuditTraceId } from "./pricingAuditTraceId.js";

const LS_KEY_CAPTURE = "PRICING_ENGINE_AUDIT_CAPTURE";
const LS_KEY_DISABLE_DEV = "PRICING_ENGINE_AUDIT_DISABLE_DEV";
const MAX_ENTRIES = 300;

/** @type {import('./pricingEngineAuditTypes').PricingEngineAuditEntry[]} */
let entries = [];
let version = 0;
const listeners = new Set();

/**
 * Cached snapshot for the same `version` so useSyncExternalStore sees a stable reference
 * when data did not change (Object.is). A fresh object every getSnapshot() causes infinite re-renders.
 */
let snapshotCache = { version: -1, items: [] };

export function jsonSafe(value) {
  try {
    return JSON.parse(
      JSON.stringify(value, (_k, v) => (typeof v === "function" ? undefined : v))
    );
  } catch {
    return null;
  }
}

export function shouldCapturePricingEngineAudit() {
  try {
    if (typeof import.meta !== "undefined" && import.meta.env?.MODE === "test") {
      return false;
    }
    if (typeof import.meta !== "undefined" && import.meta.env?.DEV) {
      if (localStorage.getItem(LS_KEY_DISABLE_DEV) === "1") return false;
      return true;
    }
    return localStorage.getItem(LS_KEY_CAPTURE) === "1";
  } catch {
    return false;
  }
}

export function setPricingEngineAuditCaptureEnabled(enabled) {
  try {
    localStorage.setItem(LS_KEY_CAPTURE, enabled ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export function setPricingEngineAuditDisableDevCapture(disable) {
  try {
    localStorage.setItem(LS_KEY_DISABLE_DEV, disable ? "1" : "0");
  } catch {
    /* ignore */
  }
}

export function getPricingEngineAuditCaptureSettings() {
  try {
    return {
      manualProd: localStorage.getItem(LS_KEY_CAPTURE) === "1",
      disableDev: localStorage.getItem(LS_KEY_DISABLE_DEV) === "1",
      isDev: Boolean(import.meta.env?.DEV),
    };
  } catch {
    return { manualProd: false, disableDev: false, isDev: false };
  }
}

function bump() {
  version += 1;
  listeners.forEach((fn) => {
    try {
      fn();
    } catch {
      /* ignore */
    }
  });
}

/** @param {() => void} listener */
export function subscribePricingEngineAudit(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function getPricingEngineAuditSnapshot() {
  if (snapshotCache.version !== version) {
    snapshotCache = { version, items: [...entries] };
  }
  return snapshotCache;
}

export function clearPricingEngineAuditEntries() {
  entries = [];
  bump();
}

/**
 * @param {unknown} payload
 */
export function importPricingEngineAuditEntries(payload) {
  const list = Array.isArray(payload) ? payload : payload ? [payload] : [];
  for (let i = list.length - 1; i >= 0; i -= 1) {
    const e = list[i];
    if (e && typeof e === "object" && e.id && e.kind) {
      entries.unshift(e);
    }
  }
  while (entries.length > MAX_ENTRIES) {
    entries.pop();
  }
  bump();
}

/**
 * @param {import('./pricingEngineAuditTypes').PricingEngineAuditEntry} entry
 */
function pushEntry(entry) {
  entries.unshift(entry);
  if (entries.length > MAX_ENTRIES) {
    entries.length = MAX_ENTRIES;
  }
  bump();
}

/**
 * @param {{
 *   meta: import('./pricingEngineAuditTypes').PricingAuditMeta | null,
 *   input: Record<string, unknown> | null,
 *   result: import('./pricingEngineAuditTypes').UnifiedBookingPricingEngineResult | null,
 * }} payload
 */
export function recordUnifiedBookingPricingAudit({ meta, input, result }) {
  pushEntry({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    recordedAt: new Date().toISOString(),
    kind: "unified_booking",
    meta: meta ?? null,
    input: input ?? null,
    result: result ?? null,
  });
}

/**
 * @param {{
 *   meta: import('./pricingEngineAuditTypes').PricingAuditMeta | null,
 *   input: Record<string, unknown> | null,
 *   result: import('./pricingEngineAuditTypes').PaymentInstallmentEngineResult | null,
 * }} payload
 */
export function recordPaymentInstallmentPricingAudit({ meta, input, result }) {
  pushEntry({
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
    recordedAt: new Date().toISOString(),
    kind: "payment_installment",
    meta: meta ?? null,
    input: input ?? null,
    result: result ?? null,
  });
}

/**
 * רישום גנרי (למשל מחיר מלון שלא עובר ב־runUnifiedBookingPricing).
 * @param {{
 *   kind: string,
 *   meta: import('./pricingEngineAuditTypes').PricingAuditMeta | Record<string, unknown> | null,
 *   input: Record<string, unknown> | null,
 *   result: Record<string, unknown> | null,
 * }} payload
 */
export function recordPricingAudit({ kind, meta, input, result }) {
  if (!shouldCapturePricingEngineAudit()) return;
  try {
    const rawMeta =
      meta && typeof meta === "object" ? { ...meta } : {};
    const traceId = resolvePricingAuditTraceId(rawMeta);
    if (rawMeta.traceId == null || String(rawMeta.traceId).trim() === "") {
      rawMeta.traceId = traceId;
    }
    const mergedMeta = compactPricingAuditMeta(rawMeta) ?? { traceId };
    pushEntry({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`,
      recordedAt: new Date().toISOString(),
      kind,
      meta: mergedMeta,
      input: jsonSafe(input),
      result: jsonSafe(result),
    });
  } catch (e) {
    console.warn("[pricing-audit] recordPricingAudit failed", e);
  }
}
